import React from 'react';
import { toast } from 'react-toastify';
import { useAuthSlice } from '../../features/auth/authSlice';
import { useGuildsSlice } from '../../features/guilds/guildsSlice';
import { useUISlice } from '../../features/ui/uiSlice';
import { useGetGuildRolesQuery } from '../../features/guilds/guildsApi';
import {
  useGetTemplatesQuery,
  useGetTemplateRolesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
} from '../../features/templates/templatesApi';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { TemplateCard } from './TemplateCard';
import { TemplateForm } from './TemplateForm';
import { Button } from '../ui/button';
import type { SelectedRole } from '../../types';

export const TemplateList: React.FC = () => {
  const authSlice = useAuthSlice();
  const guildsSlice = useGuildsSlice();
  const uiSlice = useUISlice();

  // Fetch templates for selected guild
  const {
    data: templates = [],
    isLoading: templatesLoading,
    error: templatesError,
  } = useGetTemplatesQuery(guildsSlice.selectedGuild!, {
    skip: !guildsSlice.selectedGuild,
  });

  // Fetch roles for selected guild (for create/edit form)
  const {
    data: roles = [],
    isLoading: rolesLoading,
    error: rolesError,
  } = useGetGuildRolesQuery(guildsSlice.selectedGuild!, {
    skip: !guildsSlice.selectedGuild || (uiSlice.activeTab !== 'create' && !uiSlice.editingTemplateId),
  });

  // Fetch template roles for editing
  const {
    data: templateRoles = [],
    isLoading: templateRolesLoading,
  } = useGetTemplateRolesQuery(uiSlice.editingTemplateId!, {
    skip: !uiSlice.editingTemplateId,
  });

  // Mutations
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation();

  const handleCreateTemplate = async (
    name: string,
    messageContent: string,
    selectedRoles: SelectedRole[],
    selectionMode: 'single' | 'multi',
    webhookUsername: string | null,
    webhookAvatarUrl: string | null
  ) => {
    if (!guildsSlice.selectedGuild || !authSlice.auth?.user) return;

    try {
      await createTemplate({
        guild_id: guildsSlice.selectedGuild,
        created_by: authSlice.auth.user.id,
        name,
        message_content: messageContent,
        selection_mode: selectionMode,
        webhook_username: webhookUsername,
        webhook_avatar_url: webhookAvatarUrl,
        roles: selectedRoles.map((role) => ({
          role_id: role.roleId,
          role_name: role.name,
          label: role.name,
          description: '',
          emoji: role.emoji,
        })),
      }).unwrap();

      toast.success('Template created successfully!');
      uiSlice.setActiveTab('list');
    } catch (err: any) {
      const message = err?.data?.error || 'Failed to create template. Please try again.';
      toast.error(message);
      throw err;
    }
  };

  const handleUpdateTemplate = async (
    name: string,
    messageContent: string,
    selectedRoles: SelectedRole[],
    selectionMode: 'single' | 'multi',
    webhookUsername: string | null,
    webhookAvatarUrl: string | null
  ) => {
    if (!uiSlice.editingTemplateId) return;

    // Detect branding changes so we can surface the new-sends-only caveat.
    const original = templates.find(t => t.id === uiSlice.editingTemplateId);
    const brandingChanged =
      !!original &&
      ((original.webhook_username ?? null) !== webhookUsername ||
        (original.webhook_avatar_url ?? null) !== webhookAvatarUrl);

    try {
      // Update template - backend automatically cascades to Discord messages
      const result = await updateTemplate({
        templateId: uiSlice.editingTemplateId,
        data: {
          name,
          message_content: messageContent,
          selection_mode: selectionMode,
          webhook_username: webhookUsername,
          webhook_avatar_url: webhookAvatarUrl,
          roles: selectedRoles.map((role) => ({
            role_id: role.roleId,
            role_name: role.name,
            label: role.name,
            description: '',
            emoji: role.emoji,
          })),
        }
      }).unwrap();

      // Show appropriate success message based on message updates
      if (result.messagesUpdated && result.messagesUpdated > 0) {
        if (result.messageErrors && result.messageErrors > 0) {
          toast.warning(
            `Template updated! ${result.messagesUpdated} Discord message(s) updated, ${result.messageErrors} failed.`,
            { autoClose: 5000 }
          );
        } else {
          toast.success(`Template and ${result.messagesUpdated} Discord message(s) updated!`);
        }
      } else if (result.warning) {
        toast.warning(`Template updated! ${result.warning}`);
      } else {
        toast.success('Template updated successfully!');
      }

      if (brandingChanged) {
        toast.info(
          'Branding changes only apply to new messages — Discord locks sender name and avatar at send time.',
          { autoClose: 6000 }
        );
      }

      uiSlice.setEditingTemplateId(null);
      uiSlice.setActiveTab('list');
    } catch (err: any) {
      console.error('Update error:', err);
      const message = err?.data?.error || 'Failed to update template. Please try again.';
      toast.error(message);
      throw err;
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!guildsSlice.selectedGuild) return;

    try {
      await deleteTemplate({ guildId: guildsSlice.selectedGuild, templateId }).unwrap();
      toast.success('Template deleted successfully!');
    } catch (err) {
      toast.error('Failed to delete template. Please try again.');
    }
  };

  const handleEditTemplate = (templateId: string) => {
    uiSlice.setEditingTemplateId(templateId);
    uiSlice.setActiveTab('create');
  };

  const handleCancelForm = () => {
    uiSlice.setEditingTemplateId(null);
    uiSlice.setActiveTab('list');
  };

  if (!guildsSlice.selectedGuild) {
    return null;
  }

  const isLoading = templatesLoading || rolesLoading || isCreating || isUpdating || isDeleting;
  const isEditMode = !!uiSlice.editingTemplateId;
  const editingTemplate = isEditMode ? templates.find(t => t.id === uiSlice.editingTemplateId) : null;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      {uiSlice.activeTab === 'create' ? (
        <>
          {rolesLoading && <LoadingSpinner message="Loading roles..." />}
          {templateRolesLoading && <LoadingSpinner message="Loading template..." />}
          {rolesError && (
            <ErrorMessage message="Failed to load roles. Please try again." />
          )}
          {!rolesLoading && !templateRolesLoading && !rolesError && (
            <TemplateForm
              guildId={guildsSlice.selectedGuild}
              roles={roles}
              onSubmit={isEditMode ? handleUpdateTemplate : handleCreateTemplate}
              onCancel={handleCancelForm}
              initialData={isEditMode && editingTemplate ? {
                name: editingTemplate.name,
                messageContent: editingTemplate.message_content,
                selectionMode: editingTemplate.selection_mode || 'multi',
                webhookUsername: editingTemplate.webhook_username ?? null,
                webhookAvatarUrl: editingTemplate.webhook_avatar_url ?? null,
                selectedRoles: templateRoles.map(role => ({
                  roleId: role.role_id,
                  name: role.role_name,
                  emoji: role.emoji || '🎭',
                  color: roles.find(r => r.id === role.role_id)?.color || 0,
                })),
              } : undefined}
              isEditMode={isEditMode}
            />
          )}
        </>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-on-surface mb-2">
                Role Templates
              </h2>
              <p className="text-on-surface-variant text-sm max-w-lg">
                Create and manage role selection templates. Send them to channels so members can self-assign roles.
              </p>
            </div>
            <Button
              onClick={() => uiSlice.setActiveTab('create')}
              disabled={isLoading}
              className="shrink-0"
            >
              <span className="material-symbols-outlined text-lg">add_circle</span>
              Create Template
            </Button>
          </div>

          {/* Loading */}
          {templatesLoading && <LoadingSpinner message="Loading templates..." />}

          {/* Error */}
          {templatesError && (
            <ErrorMessage message="Failed to load templates. Please try again." />
          )}

          {/* Empty State */}
          {!templatesLoading && !templatesError && templates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-surface-container-low mb-6">
                <span className="material-symbols-outlined text-5xl text-on-surface-variant/40">
                  folder_off
                </span>
              </div>
              <h3 className="text-xl font-bold text-on-surface mb-2">No templates yet</h3>
              <p className="text-on-surface-variant text-sm max-w-md mb-8">
                Templates let you create reusable role selection menus for your server. Get started by creating your first one.
              </p>
              <Button onClick={() => uiSlice.setActiveTab('create')}>
                <span className="material-symbols-outlined text-lg">add_circle</span>
                Create Your First Template
              </Button>
            </div>
          )}

          {/* Template Grid */}
          {!templatesLoading && !templatesError && templates.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onDelete={handleDeleteTemplate}
                  onEdit={handleEditTemplate}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
